import { AidaPayloadService } from './../../shared/services/aida/aida-payload/aida-payload.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  Renderer2,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';

import { Constants } from '../../shared/constants/create-survey.constants';
import {
  Qualification,
  QualificationData,
  QuotaFlags,
} from '../../shared/interfaces/qualification.interface';
import { QuotaModalComponent } from '../quota-modal/quota-modal.component';
import { SelectedQuota } from '../../shared/interfaces/quota.interface';
import { Flexible } from '../../shared/interfaces/create-surveys.interface';
import { QuotaModalService } from '../quota-modal/quota-modal.service';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { AidaPayload } from '../../shared/interfaces/aida.interface';
import { notifyMessage } from '../../shared/constants/notify-message';
import { Subscription } from 'rxjs';
import { AidaSurveyQuotasEditInspector } from '../../shared/domain/aida-survey-quotas-edit-inspector';
import { QuotaFlagsComputer } from '../domain/quota-flags-computer';

let itemCount = 0;
@Component({
  selector: 'ps-ui-multiselect-input-button',
  templateUrl: './multiselect-input-button.component.html',
  styleUrls: ['./multiselect-input-button.component.css'],
})
export class MultiselectInputButtonComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('toggleButton')
  toggleButton!: ElementRef;
  @ViewChild('list')
  list!: ElementRef;

  public quotaFlags: QuotaFlags = {
    add: true,
    edit: false,
    census: false,
    delete: false,
  };
  public isOpen: boolean = false;
  public buttonLabel: string = 'All';
  public parentSelector: boolean = true;
  public parentSelectorLabel: string = 'Deselect All';
  public id = itemCount++;
  public disableCensusBtn: boolean = false;
  public isEditQuotaDisabled: boolean = false;
  public showChildrenTooltip: boolean = false;
  public isSelectedForAida: boolean = false;
  private _aida: AidaPayload = { enabled: false, selected_quota_ids: [] };
  private _subscription: Subscription = new Subscription();

  @Input() isMcSurvey: boolean = false;
  @Input() complete!: number;
  @Input() clickBalance!: number;
  @Input() addQuotaFlag: boolean = true;
  @Input() quotas?: SelectedQuota[];
  @Input() flexible?: Flexible;
  @Input() qualification!: Qualification;
  @Input() isAdvanceTargetingParent?: boolean = false;
  @Input() isQlcLiveSurvey: boolean = false;
  @Input() changeCompletes: EventEmitter<number> = new EventEmitter<number>();
  @Input() hasNestedGroup: boolean = false;
  @Input() isLiveSurveyEdit: boolean = false;
  @Input() disabled: boolean = false;

  @Output() readonly selectionChange = new EventEmitter();
  @Output() readonly quotaChange = new EventEmitter();

  constructor(
    private _renderer: Renderer2,
    private _modal: ModalService,
    private _aidaPayloadService: AidaPayloadService,
    private _quotaModalService: QuotaModalService
  ) {
    this._renderer.listen('window', 'click', (event: Event) => {
      /**
       * Only run when toggleButton is not clicked
       * If we don't check this, all clicks (even on the toggle button) gets into this
       * section which in the result we might never see the menu open!
       * And the menu itself is checked here, and it's where we check just outside of
       * the menu and button the condition abbove must close the menu
       */
      if (
        event.target !== this.toggleButton.nativeElement &&
        !this.list.nativeElement.contains(event.target)
      ) {
        this.isOpen = false;
      }
    });
  }

  ngOnInit(): void {
    // set default flexibility in case of undefined.
    if (!this.flexible) {
      this.flexible = {
        enable: true,
        value: 0,
      };
    }

    if (!this.addQuotaFlag) {
      this.quotaFlags.add = false;
    }
    this._setQuotaFlags();
    if (
      this.qualification.qualification_code ===
        QUALIFICATION_CODES.CHILD_AGE_GENDER &&
      this.quotas?.length
    ) {
      this.quotas = this._quotaModalService.mapChildAgeGender(this.quotas);
    }

    if (
      this.qualification.qualification_code === QUALIFICATION_CODES.CHILDREN &&
      this.quotas?.length
    ) {
      // add empty range row on top for child Children in live edit page
      this.quotas?.unshift({
        max: 0,
        min: 0,
        number: 0,
        percentage: 0,
        maximum: 0,
        minimum: 0,
        gender: undefined,
        text: 'Have Children',
        ageunit: undefined,
      });
    }

    this._subscription.add(
      this.changeCompletes.subscribe((newCompletes) => {
        if (this.quotas) {
          this.quotas = this._quotaModalService.reDistributeQuotaQuantites(
            this.quotas,
            newCompletes,
            this.flexible
          );

          this.quotaChange.emit({
            msg: Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
            qualification: this.qualification,
            quotas: this.quotas,
            flexible: this.flexible,
          });
        }
      })
    );
  }

  ngOnChanges() {
    this._handleAidaSubscription();
    this._updateLabels();
    this._setQuotaFlags();
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  async onSelectChange($event: any, selectedItem?: QualificationData) {
    if (this.isSelectedForAida) {
      const target = $event.target as HTMLInputElement;
      setTimeout(() => {
        target.checked = !target.checked;
        if (selectedItem) {
          selectedItem.selected = target.checked;
        } else {
          this.parentSelector = !this.parentSelector;
        }
        this._aidaPayloadService.sendAidaError(
          notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
        );
      }, 0);
      return;
    }
    const id = $event.target.value;
    let isChecked = $event.target.checked;
    const [isProceed, deleteQuotaOnQualificationChange] =
      await this._quotaModalService.quotaConfirmationChecked(
        this.quotas,
        this.qualification.hasNestedQuotas
      );
    if (isProceed) {
      // proceed with selection
      this.qualification.data = this.qualification.data.map((item) => {
        if (item.id == id) {
          item.selected = isChecked;
          this.parentSelector = false;
          return item;
        }
        if (id == -1) {
          item.selected = this.parentSelector;
          return item;
        }
        return item;
      });
      this._updateLabels();
      this.selectionChange.emit(this.qualification);
    } else {
      // reverse action here
      if (Number(id) === -1) {
        // its for selectAll/Deselect All
        this.parentSelector = !this.parentSelector;
      } else {
        // single option like male/female
        isChecked = !isChecked;
      }
      this.qualification.data = this.qualification.data.map((item) => {
        if (item.id == id) {
          item.selected = isChecked;
          return item;
        }
        return item;
      });
    }
    // delete quota if confirmed YES
    if (deleteQuotaOnQualificationChange) {
      this._deleteQuotasResets();
    }
  }

  private _updateLabels() {
    let isHaveChildrenQuota = false;
    const selectedCount = this.qualification.data.reduce((n, item) => {
      if (item.selected) {
        isHaveChildrenQuota = !!(item.id === Constants.CHILDREN.HAVE_CHILDREN);
        return n + 1;
      }
      return n;
    }, 0);
    //Disabling the Edit button only for one selected value
    if (selectedCount == 1 && !isHaveChildrenQuota) {
      this.isEditQuotaDisabled = true;
    } else {
      this.isEditQuotaDisabled = false;
    }
    if (selectedCount === 0) {
      this.buttonLabel = 'Select';
      this.parentSelectorLabel = 'Select All';
    } else if (selectedCount === this.qualification.data.length) {
      this.buttonLabel = 'All';
      this.parentSelectorLabel = 'Deselect All';
      this.parentSelector = true;
    } else {
      this.buttonLabel = `${selectedCount} selected`;
      this.parentSelectorLabel = 'Select All';
    }

    this.showChildrenTooltip =
      !!this.quotas?.length &&
      this.qualification.qualification_code === QUALIFICATION_CODES.CHILDREN;
  }

  private _setQuotaFlags() {
    const computer = new QuotaFlagsComputer(
      this.quotas || [],
      this.qualification
    );
    this.quotaFlags.add = computer.add();
    this.quotaFlags.edit = computer.edit();
    this.quotaFlags.delete = computer.delete();
    this.quotaFlags.census = computer.census();
    this.disableCensusBtn = !!(this.quotaFlags.census && this.quotas?.length);
  }

  openMultiselectInputQuotaModal() {
    if (this._quotaModalService.inValidComplete(this.complete)) {
      return;
    }
    const modalRef = this._modal.open(QuotaModalComponent, {
      data: {
        quotas: this.quotas,
        complete: this.complete,
        flexible: this.flexible,
        clickBalance: this.clickBalance,
        qualification: this.qualification,
        isCensusApplied: this.disableCensusBtn,
        hasNestedGroup: this.hasNestedGroup,
        isLiveSurveyEdit: this.isLiveSurveyEdit,
        isMcSurvey: this.isMcSurvey,
        isSelectedForAida: this.isSelectedForAida,
      },
      width: '80%',
    });

    modalRef.onClose$.subscribe((response) => {
      if (
        [
          Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          Constants.QUOTA_MODAL_ACTION.APPLY_NEST_QUOTA,
        ].includes(response?.msg)
      ) {
        this.quotas = response.quotas;
        this.flexible = response.flexible;
        const showCensus = ![
          QUALIFICATION_CODES.CHILD_AGE_GENDER,
          QUALIFICATION_CODES.CHILDREN,
        ].includes(this.qualification.qualification_code);
        this.quotaFlags = {
          add: false,
          edit: true,
          census: showCensus,
          delete: true,
        };
        this.quotaChange.emit(response);
        this.disableCensusBtn = true;
        this._updateLabels();
      }
    });
  }

  checkForMcSurveys(isDelete: boolean = false) {
    const applyToOnlyThisCountry = () => {
      if (isDelete) {
        this._deleteQuotas();
      } else {
        this.applyMultiselectCensus();
      }
    };

    const applyForAllCountries = () => {
      if (isDelete) {
        this._deleteQuotas(true);
      } else {
        this.applyMultiselectCensus(true);
      }
    };

    if (this.isMcSurvey) {
      const text = isDelete
        ? Constants.CONFIRM_MODAL_TEXT.DELETE_CENSUS_TO_ALL_MC
        : Constants.CONFIRM_MODAL_TEXT.CENSUS_APPLY_TO_ALL_MC;
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text,
          confirmButtonLabel: 'Apply to all locales',
          cancelButtonLabel: 'Apply only in this locale',
          showCloseButton: true,
        },
        width: '50%',
      });

      modalRef.onClose$.subscribe((msg) => {
        if (msg === 'ok') {
          applyForAllCountries();
        } else if (!!msg) {
          applyToOnlyThisCountry();
        }
      });
    } else {
      applyToOnlyThisCountry();
    }
  }

  checkAndApplyMultiselectInputCensus() {
    if (this.isMcSurvey) {
      return this.checkForMcSurveys(false);
    }

    if (this.quotaFlags.edit && !this.disableCensusBtn) {
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: Constants.CONFIRM_MODAL_TEXT.ADD_CENSUS,
        },
      });

      modalRef.onClose$.subscribe((mssg) => {
        if (mssg == 'ok') {
          this.applyMultiselectCensus();
        }
      });
    } else {
      this.applyMultiselectCensus();
    }
  }

  public applyMultiselectCensus(
    isMc: boolean = false,
    qualification: Qualification = this.qualification,
    quotas: SelectedQuota[] = this.quotas ?? [],
    flexible = this.flexible,
    complete: number = this.complete,
    isOutSideCall: boolean = false
  ) {
    if (this._quotaModalService.inValidComplete(this.complete)) {
      return;
    }
    const distributedQuotas = this._quotaModalService.distributeQuotas(
      qualification,
      complete,
      flexible
    );
    const mappedQuotas = this._quotaModalService.adjustQuotaQuantites(
      distributedQuotas,
      complete,
      flexible,
      true
    );

    if (mappedQuotas.length) {
      quotas = mappedQuotas;
      this.disableCensusBtn = true;
      const showCensus =
        qualification.qualification_code !== QUALIFICATION_CODES.CHILDREN;
      qualification.data = qualification.data.map((item) => {
        const census = qualification.census?.data?.find(
          (singleCensus) => singleCensus.condition_code === item.id
        );
        if (census) {
          item.selected = !!census?.rep_value;
        } else {
          item.selected = true;
        }
        return item;
      });

      if (isOutSideCall) {
        return quotas;
      }

      this.quotas = quotas;
      this.qualification = qualification;

      this._updateLabels();
      this.selectionChange.emit(this.qualification);
      this.quotaFlags = {
        add: false,
        edit: true,
        census: showCensus,
        delete: true,
      };
      return this.quotaChange.emit({
        msg: 'applyCensus',
        qualification: this.qualification,
        quotas: this.quotas,
        flexible,
        isMc,
      });
    }
  }

  deleteMultiselectInputQuotas() {
    if (this.isSelectedForAida) {
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }

    if (this.isMcSurvey) {
      return this.checkForMcSurveys(true);
    }

    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL_TEXT.DELETE_QUOTA.replace(
          '<QUALIFICATION_NAME>',
          this.qualification.qualification_name
        ),
        confirmButtonLabel: 'Yes',
      },
    });

    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this._deleteQuotas();
      }
    });
  }

  private _deleteQuotas(isMc: boolean = false) {
    this.quotas = [];
    this.disableCensusBtn = false;
    const showCensus =
      this.qualification.qualification_code !== QUALIFICATION_CODES.CHILDREN;
    this.quotaFlags = {
      add: true,
      edit: false,
      census: showCensus,
      delete: false,
    };
    this.quotaChange.emit({
      msg: 'deleteQuota',
      qualification: this.qualification,
      quotas: this.quotas,
      flexible: this.flexible,
      isMc,
    });
    this._updateLabels();
    // reset multiselect to all options selected on children qualification
    if (
      this.qualification.qualification_code === QUALIFICATION_CODES.CHILDREN
    ) {
      this.onSelectChange({ target: { value: -1, isChecked: true } });
    }
  }

  private _deleteQuotasResets() {
    this.quotas = [];
    this._resetQuotaFlags();
    this.quotaChange.emit({
      msg: 'deleteQuota',
      qualification: this.qualification,
      quotas: this.quotas,
      flexible: this.flexible,
    });
  }

  private _resetQuotaFlags() {
    this.disableCensusBtn = false;
    if (
      this.qualification.qualification_code === QUALIFICATION_CODES.CHILDREN
    ) {
      this.showChildrenTooltip = false;
    }
    this.quotaFlags = {
      add: true,
      edit: false,
      census: this._quotaModalService.setCensusFlag(this.qualification),
      delete: false,
    };
  }

  private _handleAidaSubscription() {
    const _payloadSubscription = this._aidaPayloadService
      .getAidaLiveEditPayload()
      .subscribe((payload) => {
        this._aida = payload;
        this.isSelectedForAida = new AidaSurveyQuotasEditInspector(
          this._aida,
          this.quotas,
          this.qualification.hasNestedQuotas
        ).notEditable();
      });
    this._subscription.add(_payloadSubscription);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
